import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  loginUser, 
  selectLoginState, 
  resetLoginState, 
  fetchCartItems, 
  fetchWithcookieData, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn 
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { LoginFormData, LoginValidationErrors } from './Login.types';

/**
 * Login component logic hook
 * Handles all login functionality and state management
 */
export function useLoginLogic(componentData: ComponentData, mode: ComponentRenderMode) {
  const { t } = useTranslation();
  const dispatch = useDispatch() as any;
  
  // Redux state
  const { loading, loginSuccess, loginError, validationErrors: reduxValidationErrors } = useSelector(selectLoginState);
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  const withcookieDataLoading = useSelector((state: any) => state.withcookie.withcookieDataLoading);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  
  // Theme data
  const theme = withcookieData?.skin?.theme?.colorset || {};
  
  // API call ref
  const fetchWithcookieDataRef = useRef(false);
  
  // Local state
  const [formData, setFormData] = useState<LoginFormData>({
    user_id: "",
    password: ""
  });
  const [localValidationErrors, setLocalValidationErrors] = useState<LoginValidationErrors>({});
  
  // Merge validation errors
  const validationErrors = { ...localValidationErrors, ...reduxValidationErrors };
  
  // Extract redirect URL from component props
  const redirectUrl = componentData.componentProps?.redirectPath ||  // 먼저 redirectPath 확인
                     componentData.componentProps?.redirectUrl || 
                     componentData.props?.redirectPath || 
                     componentData.props?.redirectUrl || 
                     '/';
  

  // Load skin data if not available
  useEffect(() => {
    if (withcookieData?.skin?.theme || withcookieDataLoading || fetchWithcookieDataRef.current) {
      return;
    }
    
    fetchWithcookieDataRef.current = true;
    dispatch(fetchWithcookieData())
      .finally(() => {
        fetchWithcookieDataRef.current = false;
      });
  }, [withcookieData, withcookieDataLoading, dispatch]);
  
  // Reset login state on unmount
  useEffect(() => {
    return () => {
      dispatch(resetLoginState());
    };
  }, [dispatch]);
  
  // Redirect if already logged in (not in editor mode)
  useEffect(() => {
    if (mode === 'editor') return;
    
    const currentPath = window.location.pathname;
    if (currentPath.includes("/edit")) return;
    
    if (isUserLoggedIn) {
      const companyId = withcookieData?.skin?.company?.id;
      const isMobileOrTablet = window.innerWidth < 900 || window.innerWidth < 1200;
      
      if ((companyId === 24 || companyId === 26) && isMobileOrTablet) {
        window.location.href = '/finapp';
      } else if ((companyId === 27 || companyId === 28) && isMobileOrTablet) {
        window.location.href = '/gpc';
      } else {
        window.location.href = redirectUrl;
      }
    }
  }, [isUserLoggedIn, redirectUrl, withcookieData, mode]);
  
  // Handle login success
  useEffect(() => {
    if (loginSuccess && mode !== 'editor') {
      dispatch(fetchCartItems());
      
      const redirectTimer = setTimeout(() => {
        const companyId = withcookieData?.skin?.company?.id;
        const skinId = withcookieData?.skin?.id;
        const isMobileOrTablet = window.innerWidth < 900 || window.innerWidth < 1200;
        
        if ((companyId === 24  || companyId === 26) && isMobileOrTablet) {
          window.location.href = '/finapp';
        } else if ((companyId === 27 || companyId === 28 || skinId === 317) && isMobileOrTablet) {
          window.location.href = '/gpc';
        } else {
          window.location.href = redirectUrl;
        }
      }, 1000);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [loginSuccess, dispatch, redirectUrl, withcookieData, mode]);
  
  // Field validation
  const validateField = useCallback((id: string, value: string) => {
    setLocalValidationErrors(prevErrors => {
      let errors = {...prevErrors};
      
      if (id === 'user_id') {
        if (!value.trim()) {
          errors.user_id = t('아이디를 입력해주세요');
        } else if (value.length < 2 || value.length > 20) {
          errors.user_id = t('아이디는 2~20자로 입력해주세요');
        } else {
          delete errors.user_id;
        }
      }
      
      if (id === 'password') {
        if (!value) {
          errors.password = t('비밀번호를 입력해주세요');
        } else {
          delete errors.password;
        }
      }
      
      return errors;
    });
  }, [t]);
  
  // Form validation
  const validateForm = useCallback(() => {
    const errors: LoginValidationErrors = {};
    const { user_id, password } = formData;
    
    if (!user_id.trim()) {
      errors.user_id = t('아이디를 입력해주세요');
    } else if (user_id.length < 2 || user_id.length > 20) {
      errors.user_id = t('아이디는 2~20자로 입력해주세요');
    }
    
    if (!password) {
      errors.password = t('비밀번호를 입력해주세요');
    }
    
    setLocalValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);
  
  // Actions
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    
    setFormData(prevData => ({
      ...prevData,
      [id]: value
    }));
    validateField(id, value);
  }, [validateField]);
  
  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (mode === 'editor') {
      // In editor mode, just show demo behavior
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    dispatch(loginUser(formData));
  }, [mode, formData, dispatch, validateForm]);
  
  const handleSignupClick = useCallback(() => {
    if (mode === 'editor') {
      return;
    }
    window.location.href = '/signup';
  }, [mode]);
  
  // Return data and actions in proper structure
  return {
    data: {
      // Form state
      formData,
      validationErrors,
      loading,
      loginSuccess,
      loginError,
      
      // Theme and global state
      theme,
      withcookieData,
      isUserLoggedIn,
      isAdminLoggedIn,
      
      // Component props
      redirectUrl: componentData.componentProps?.redirectUrl || '/',
      backgroundType: componentData.componentProps?.backgroundType || 'image',
      backgroundUrl: componentData.componentProps?.backgroundUrl || '',
      title: componentData.componentProps?.title || '로그인',
      buttonColor: componentData.componentProps?.buttonColor,
      titleColor: componentData.componentProps?.titleColor || '#333',
      labelColor: componentData.componentProps?.labelColor || '#333',
      inputTextColor: componentData.componentProps?.inputTextColor || '#333',
      style: componentData.style || {},
      className: componentData.className || '',
      
      // Editor mode
      isEditor: mode === 'editor',
      isPreview: mode === 'preview'
    },
    actions: {
      handleChange,
      handleSubmit,
      handleSignupClick
    }
  };
}